import React, { useContext, useEffect, useState, useRef } from "react";
import { Box, Stack, Reel, Text } from "@thecb/components";
import { ThemeContext } from "styled-components";
import { getPageCount } from "/util/general";
import AlertBar from "/components/alert-bar";
import { useActivityHistory } from "./useActivityHistory";
import ActivityTable from "./ActivityTable";
import { Center, Spinner, Pagination } from "@thecb/components";
import {
  FONT_WEIGHT_SEMIBOLD,
  SHADOW_BASE_STANDARD
} from "/constants/style_constants";
import { WHITE, GHOST_GREY, CHARADE_GREY } from "/constants/colors";

const ActivityHistory = ({
  alertBars,
  actions,
  resources,
  resourcesActions,
  customerManagement,
  themeValues,
  shouldRefresh
}) => {
  const [currentPage, setCurrentPageState] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const tableContainerRef = useRef(null);

  const setCurrentPage = ({ pageNumber }) => {
    setCurrentPageState(pageNumber);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };

  const { auditHistoryPage } = useActivityHistory(
    resources,
    resourcesActions,
    currentPage,
    shouldRefresh
  );
  const themeContext = useContext(ThemeContext);
  const { isMobile } = themeContext;

  useEffect(() => {
    setPageCount(
      getPageCount(auditHistoryPage?.total, auditHistoryPage?.perPage)
    );
  }, [auditHistoryPage?.total]);

  const activities = auditHistoryPage?.auditEntries || [];
  const status = auditHistoryPage?.isLoading
    ? "loading"
    : auditHistoryPage?.isSuccess
    ? "success"
    : auditHistoryPage?.isFailure
    ? "error"
    : "not found";

  const { auditHistoryAlertBar: alertBar = { alerts: [] } } = alertBars || {};
  const { alertBars: { auditHistoryAlertBar: alertBarActions = {} } = {} } =
    actions || {};

  const renderActivityContent = status => {
    switch (status) {
      case "loading":
        return (
          <Box padding="3rem" maxWidth="25rem">
            <Center intrinsic>
              <Spinner size={100} />
            </Center>
          </Box>
        );
      case "success":
        return (
          <Stack childGap="1rem">
            <Box
              ref={tableContainerRef}
              background={WHITE}
              padding="0"
              boxShadow={SHADOW_BASE_STANDARD}
            >
              {isMobile ? (
                <Reel padding="0">
                  <Stack fullHeight childGap="1rem">
                    <ActivityTable
                      activities={activities}
                      customerManagement={customerManagement}
                      linkColor={themeValues.activeTabAccent}
                    />
                  </Stack>
                </Reel>
              ) : (
                <Stack fullHeight childGap="1rem">
                  <ActivityTable
                    activities={activities}
                    customerManagement={customerManagement}
                    linkColor={themeValues.activeTabAccent}
                  />
                </Stack>
              )}
            </Box>
            {pageCount > 1 && (
              <Box
                background={WHITE}
                borderRadius="0.25rem"
                padding="1rem"
                boxShadow={SHADOW_BASE_STANDARD}
              >
                <Pagination
                  childGap="1rem"
                  fontWeight={FONT_WEIGHT_SEMIBOLD}
                  currentPage={currentPage}
                  pageCount={pageCount}
                  setCurrentPage={setCurrentPage}
                  pageNext={() =>
                    setCurrentPage({ pageNumber: currentPage + 1 })
                  }
                  pagePrevious={() =>
                    setCurrentPage({ pageNumber: currentPage - 1 })
                  }
                  ariaLabel="Activity History Pagination"
                />
              </Box>
            )}
          </Stack>
        );
      case "error":
        return (
          <Box
            background={WHITE}
            borderRadius="0.25rem"
            boxShadow={`0 0 0.313rem 0 ${GHOST_GREY}`}
            padding="24px"
          >
            <Text color={CHARADE_GREY}>
              Unable to load activity history. Please refresh the page to try
              again.
            </Text>
          </Box>
        );
      default:
    }
  };

  return (
    <Box padding="0 0 1rem">
      <Stack fullHeight childGap="16px">
        {alertBar?.alerts && alertBar.alerts.length > 0 && (
          <AlertBar {...alertBar} {...alertBarActions} key="alert-bar" />
        )}
        <Stack childGap="1rem">{renderActivityContent(status)}</Stack>
      </Stack>
    </Box>
  );
};

export default ActivityHistory;
